/**
 * Deal-specific pipeline card content.
 *
 * Layout:
 * ┌─────────────────────────────────┐
 * │ Deal Name                       │
 * │ 🏢 Company Name                 │
 * │                                 │
 * │ $ 4,500    📅 May 2nd, 2026    │
 * │ ─────────────────────────────── │
 * │ 👤 Owner      🕐 Last updated  │
 * └─────────────────────────────────┘
 *
 * The title row, company/contact line, and footer come from the shared
 * pipeline card parts so deal cards stay byte-equivalent to other entity
 * cards. The deal-specific bit is the amount + close-date middle row.
 */

import { Calendar } from '@untitledui/icons';
import { memo, useMemo } from 'react';

import { useOrg } from '@/contexts/OrgContext';
import type { CrmObject } from '@/types/crm';
import { getPropertyValue } from '@/types/crm';

import {
  formatCloseDate,
  formatDealAmount,
} from '../utils/deal-card-formatters';
import { CardFooter, CardTitleRow } from './PipelineCardParts';

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

export interface DealCardContentProps {
  /** The deal object */
  object: CrmObject;
  /** Associated company (first company association), if any */
  company?: CrmObject;
  /** Associated contact (first contact association), used when no company */
  contact?: CrmObject;
  /** Associated owner (user), if any */
  owner?: CrmObject;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export const DealCardContent = memo(function DealCardContent({
  object,
  company,
  contact,
  owner,
}: DealCardContentProps) {
  // Deal title prefers ahoy_name (the deal-specific user-input field) and
  // falls back to ahoy_display_name. Reverse of the universal getEntityName
  // priority because the deal schema uses ahoy_name as the canonical name
  // and ahoy_display_name as a derived alias.
  const dealName =
    getPropertyValue<string>(object.properties?.ahoy_name) ||
    getPropertyValue<string>(object.properties?.ahoy_display_name) ||
    'Untitled';

  const { currentOrg } = useOrg();
  const defaultCurrency = currentOrg?.defaultCurrency ?? 'USD';

  const amountFormatted = useMemo(() => {
    const amountRaw = getPropertyValue<unknown>(object.properties?.ahoy_amount);
    return formatDealAmount(amountRaw, defaultCurrency);
  }, [object.properties?.ahoy_amount, defaultCurrency]);

  const closeDate = useMemo(() => {
    const closeDateRaw = getPropertyValue<unknown>(
      object.properties?.ahoy_close_date
    );
    return formatCloseDate(closeDateRaw);
  }, [object.properties?.ahoy_close_date]);

  const updatedAt = useMemo(() => {
    const updatedAtRaw = getPropertyValue<unknown>(
      object.properties?.ahoy_updated_at
    );
    return updatedAtRaw !== null && updatedAtRaw !== undefined
      ? String(updatedAtRaw)
      : null;
  }, [object.properties?.ahoy_updated_at]);

  return (
    <div>
      <CardTitleRow title={dealName} company={company} contact={contact} />

      {/* ── Amount + Close date ─────────────────────────────────────── */}
      <div className='flex items-center gap-4 px-3 mt-3'>
        {amountFormatted !== null && (
          <span className='text-sm font-semibold text-[var(--color-text-primary)]'>
            {amountFormatted}
          </span>
        )}
        {closeDate && (
          <div className='flex items-center gap-1 text-sm text-[var(--color-text-secondary)]'>
            <Calendar className='size-4 text-[var(--color-text-tertiary)] flex-shrink-0' />
            <span>{closeDate}</span>
          </div>
        )}
      </div>

      <CardFooter owner={owner} updatedAt={updatedAt} />
    </div>
  );
});
